import { FlowSelectSourceType } from '@lighthouse/core'
import type { FlowNode, WeChatRefundActionConfig } from '@lighthouse/shared'
import { fromUpstreamOptions, getUpstreamNodeOption, OUTPUT_RECORD_NODES, useAtomData } from '@lighthouse/shared'
import { wechatMerchantCardLabel } from '@lighthouse/shared'
import { useNodesData } from '@xyflow/react'
import React, { useMemo } from 'react'

import { otherPlatformListAtom } from '@/atoms/application/state'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList, useDataSourceOptions } from '@/hooks/useDataSource'

import * as CM from '../styles'
import * as SC from './styles'

type WeChatRefundCardProps = {
    config: WeChatRefundActionConfig
}

const WeChatRefundCard: React.FC<WeChatRefundCardProps> = ({ config }) => {
    const { dataSourceId, selectType, nodeId } = config
    const otherPlatformList = useAtomData(otherPlatformListAtom)

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)

    const nodes = useNodesData([nodeId]) as FlowNode[]

    const upstreamNodesForSource = useMemo(() => nodes?.filter(item => OUTPUT_RECORD_NODES.has(item.data.nodeType)), [nodes])

    const nodeOptions = useMemo(
        () =>
            getUpstreamNodeOption({ allParentNodes: upstreamNodesForSource || [], dataSourceList }).map(v => ({
                ...v,
                icon: typeof v.icon === 'string' ? <SC.Icon type={v.icon} /> : v.icon
            })),
        [dataSourceList, upstreamNodesForSource]
    )

    const options = useDataSourceOptions()

    const option = useMemo(() => {
        if (selectType === FlowSelectSourceType.DATASOURCE) {
            const option = options.find(item => item.value === dataSourceId)
            return { ...option, icon: <SC.Icon type={option?.icon ?? ''} /> }
        } else if (selectType === FlowSelectSourceType.UPSTREAM) {
            return nodeOptions.find(item => item.value === nodeId)
        }
    }, [dataSourceId, nodeId, nodeOptions, options, selectType])

    const nodeTypeName = useMemo(() => {
        const data = fromUpstreamOptions.find(item => item.value === selectType)
        return data?.label
    }, [selectType])

    const merchantLabel = useMemo(
        () => wechatMerchantCardLabel(otherPlatformList, config?.paymentAccountId),
        [otherPlatformList, config?.paymentAccountId]
    )

    if (!option || !merchantLabel) {
        return null
    }

    return (
        <CM.CardItemContainer>
            <SC.TypeName>{merchantLabel}</SC.TypeName>
            <SC.TypeName>退款订单-从{nodeTypeName}中选择</SC.TypeName>

            <CM.FieldList>
                <CM.FieldItem>
                    <CM.FieldItemLeftFill>
                        {option?.icon}
                        <CM.FieldItemTitle>{option?.label}</CM.FieldItemTitle>
                    </CM.FieldItemLeftFill>
                </CM.FieldItem>
            </CM.FieldList>
        </CM.CardItemContainer>
    )
}

export default WeChatRefundCard
